import type { StudioMessage } from '../api/types';

const CATEGORY_LABELS: Record<string, string> = {
  read: '読取',
  write: '書込',
  memory: '記憶',
  ask: '質問',
  check: '検証',
  run: '解析',
  trial: '試運転',
};
const STATUS_LABELS: Record<string, string> = {
  running: '実行中',
  done: '完了',
  error: '失敗',
  cancelled: '中止',
  waiting: '回答待ち',
};
/** 要約に出す分類と見出し（0 件の分類は出さない）。 */
const SUMMARY_LABELS: Record<string, string> = {
  read: '読んだ資料',
  write: '書いたファイル',
  ask: '質問',
  memory: '記憶',
  // 試運転の呼び出しも閉じた見出しに件数を出す（T16-4）。
  trial: '試運転',
};
/** 入力の要約（6.7 の summarize_input）から、行に出す値を探す順。ref・path・name・query・url は
 * knowledge_attach の source_id（32 桁 hex）より読みやすいので先に見る。url は web_fetch_page の
 * 取得先で、成功でも失敗でもどのページを取ったかが行に出る。本文（content など）は入っていない。 */
const INPUT_KEYS = [
  'ref', 'path', 'name', 'query', 'url', 'item_id', 'memory_id', 'app_key', 'kind', 'app_id', 'source_id',
];

/** 連続するツール呼び出し（kind tool_call）の作業ログ。既定は閉じる（Claude Design）。 */
export function WorkLog({ entries }: { entries: StudioMessage[] }) {
  const count = (category: string) => entries.filter((entry) => entry.body['category'] === category).length;
  const checks = count('check');
  const summary = Object.keys(SUMMARY_LABELS)
    .map((category) => [category, count(category)] as const)
    .filter(([, n]) => n > 0)
    .map(([category, n]) => `${SUMMARY_LABELS[category]} ${n}`);
  return (
    <details className="studio-worklog">
      <summary>
        {checks > 0
          ? `作業ログ · 読んだ資料 ${count('read')} · 書いたファイル ${count('write')} · 検証 ${checks}`
          : `作業ログ${summary.length > 0 ? ` · ${summary.join(' · ')}` : ''}`}
      </summary>
      <ul className="studio-worklog-rows">
        {entries.map((entry) => {
          const status = text(entry.body['status']);
          const error = text(entry.body['error']);
          return (
            <li key={entry.id} className={`studio-worklog-row studio-worklog-${status}`}>
              <time dateTime={entry.createdAt}>{clock(entry.createdAt)}</time>
              <span className="studio-worklog-kind">{CATEGORY_LABELS[text(entry.body['category'])] ?? '操作'}</span>
              <code className="studio-worklog-tool">{text(entry.body['name'])}</code>
              <span className="studio-worklog-input">{describeInput(entry.body['input'])}</span>
              <span className="studio-worklog-status">{STATUS_LABELS[status] ?? status}</span>
              {error && <span className="hint">{error}</span>}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

function describeInput(input: unknown): string {
  if (input === null || typeof input !== 'object') return '';
  const record = input as Record<string, unknown>;
  for (const key of INPUT_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') return value;
  }
  return '';
}

function clock(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
